import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Star } from 'lucide-react';
import { reviewsAPI, reviewsExtraAPI } from '../api';
import { toast } from 'sonner';

const ReviewSection = ({ booking, onReviewCreated }) => {
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await reviewsExtraAPI.forBooking(booking.id);
        if (res.data) setExisting(res.data);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, [booking.id]);

  if (loading || booking.status !== 'completed') return null;

  if (existing) {
    return (
      <Card data-testid="review-display">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" /> Tu reseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-1 mb-2">
            {[1, 2, 3, 4, 5].map(n => (
              <Star key={n} className={`w-5 h-5 ${n <= existing.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
            ))}
          </div>
          <p className="text-gray-700 text-sm">{existing.comment}</p>
        </CardContent>
      </Card>
    );
  }

  const submit = async () => {
    if (!rating || !comment.trim()) {
      toast.error('Completa rating y comentario');
      return;
    }
    setSubmitting(true);
    try {
      const res = await reviewsAPI.create({ booking_id: booking.id, rating, comment: comment.trim() });
      toast.success('¡Gracias por tu reseña!');
      setExisting(res.data);
      onReviewCreated?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error enviando reseña');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card data-testid="review-form">
      <CardHeader>
        <CardTitle>Califica tu experiencia</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm text-gray-600 mb-2">¿Cómo fue tu experiencia?</p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(n)}
                data-testid={`star-${n}`}
              >
                <Star className={`w-8 h-8 transition ${(hover || rating) >= n ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`} />
              </button>
            ))}
          </div>
        </div>
        <Textarea
          placeholder="Cuéntanos sobre tu experiencia..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={4}
          maxLength={500}
          data-testid="review-comment-input"
        />
        <Button
          onClick={submit}
          disabled={submitting || !rating || !comment.trim()}
          className="w-full"
          data-testid="submit-review-btn"
        >
          {submitting ? 'Enviando...' : 'Publicar reseña'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default ReviewSection;
